import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, FileText } from "lucide-react";

const SLIDES = [
  {
    img: "https://images.unsplash.com/photo-1585435557343-3b092031a831?crop=entropy&cs=srgb&fm=jpg&q=85",
    tag: "Pharma Saver Sale — Up to 25% Off",
    title: "Trusted Pharmaceuticals for Pharmacies and Clinics",
    sub: "WHO-GMP certified formulations in bulk packs, with a GST invoice on every order.",
  },
  {
    img: "https://images.unsplash.com/photo-1676364423874-19c785db3e61?crop=entropy&cs=srgb&fm=jpg&q=85",
    tag: "10-BOX Wholesale Packs",
    title: "Rx Supply Without the Paperwork Chase",
    sub: "No prescription uploads — verify with your Drug License & GST right after payment.",
  },
];

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const slide = SLIDES[index];

  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-6" data-testid="hero-section">
      {/* Preload both slide images so the crossfade never hits an empty frame */}
      {SLIDES.map((s) => <img key={s.img} src={s.img} alt="" aria-hidden="true" className="hidden" />)}
      <div className="relative rounded-2xl overflow-hidden h-[380px] sm:h-[440px] shadow-xl bg-emerald-950">
        <AnimatePresence>
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
            style={{ pointerEvents: "auto" }}
            data-testid={`hero-slide-${index}`}
          >
            <img src={slide.img} alt={slide.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/85 via-emerald-950/55 to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-center px-6 sm:px-14 max-w-2xl">
              <motion.span
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                className="inline-flex w-fit bg-amber-400 text-emerald-950 text-[11px] font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full"
              >
                {slide.tag}
              </motion.span>
              <motion.h1
                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
                className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-white mt-4 text-balance"
              >
                {slide.title}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
                className="text-emerald-100/90 text-sm sm:text-base font-medium mt-3 max-w-md"
              >
                {slide.sub}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
                className="flex flex-wrap gap-3 mt-7"
              >
                <Link
                  to="/shop"
                  data-testid="hero-shop-cta"
                  className="group flex items-center gap-2 bg-emerald-700 hover:bg-emerald-600 text-white px-7 py-3.5 rounded-xl font-bold text-sm shadow-lg transition-all duration-300 active:scale-95"
                >
                  Shop Now <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/shop?rx=true"
                  data-testid="hero-rx-cta"
                  className="flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur border border-white/40 text-white px-7 py-3.5 rounded-xl font-bold text-sm transition-all duration-300 active:scale-95"
                >
                  <FileText size={15} /> Shop Rx Range
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-dot-${i}`}
              onClick={() => setIndex(i)}
              className={`h-2 rounded-full transition-all duration-300 ${i === index ? "w-7 bg-amber-400" : "w-2 bg-white/50 hover:bg-white/80"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
